import React, { useState, useLayoutEffect } from "react";

/** Components */
import { Button, TablePagination, Paper, TextField, TableContainer, Table, TableHead, TableBody, TableCell, TableRow, TableFooter, Collapse, ButtonGroup, Checkbox } from "@mui/material";
import LoadingProgress from "../../commons/LoadingProgress.component";
import CustomDatePicker from "../../commons/CustomDatePicker.js";

/** Icon */
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

/** Package */
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

/** API */
import { getConfig, saveConfig } from "../../../services/api";
import { statusColor } from "../../config";
import { setHeaderTitle } from "../../../store/layoutSlide";

/** Hook */
import { useForm } from "react-hook-form";
import { usePagination } from "../../commons/customHook";

import { useDispatch } from "react-redux";

export default function Config() {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [isSearchOpen] = useState(false);
  const [loading] = useState(false);
  const [isAllow, setIsAllow] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);
  const { setValue, handleSubmit } = useForm();
  const [page, setPage, rowsPerPage, handleChangeRowsPerPage] = usePagination();
  const [input, setInput] = useState({
    label: "",
  });
  const [isEdit, setIsEdit] = useState(false);
  const toggleEdit = () => {
    setIsEdit(!isEdit);
    if (isEdit) {
      handleSubmit(async (data) => {
        await saveConfig(data);
        fetchData();
      })();
    }
  };
  const fetchData = () => [
    getConfig().then((res) => {
      if (res.code === 403) setIsAllow(false);
      else if (res.code === 200) {
        setData(res.data);
        setValue("config", res.data);
      } else enqueueSnackbar(res.message);
    }),
  ];
  const handleEdit = (index, fieldName, value) => {
    setValue(`config.${index}.${fieldName}`, value);
  };
  // effect
  useLayoutEffect(() => {
    dispatch(setHeaderTitle("THÔNG SỐ CẤU HÌNH HỆ THỐNG"));
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <div style={{ justifyContent: "end", display: "flex", backgroundColor: "#b3deff", marginBottom: 10, marginTop: 10, height: 40 }}>
        <ButtonGroup style={{ height: 40, marginRight: 10 }}>
          <Button onClick={toggleEdit} variant="outlined">
            {isEdit ? <SaveIcon /> : <EditIcon />}
          </Button>
        </ButtonGroup>
      </div>
      <div>
        <Collapse in={isSearchOpen}>
          <TextField
            label={t("Cấu hình")}
            value={input.label || ""}
            onChange={(e) => setInput((prev) => ({ ...prev, label: e.target.value || null }))}
            style={{ margin: "5px" }}
            InputProps={{ style: { minWidth: "200px" } }}
          />
        </Collapse>
        <div style={{ display: "flex" }}>
          {loading ? (
            <LoadingProgress />
          ) : (
            <TableContainer component={Paper} style={{ width: "98%", marginLeft: 10 }} sx={{ maxHeight: 800 }}>
              <Table>
                <TableHead style={{ backgroundColor: "#f0f3f5" }}>
                  <TableRow>
                    <TableCell style={{ border: "1px solid lightgray" }} align="center">
                      ID
                    </TableCell>
                    <TableCell style={{ border: "1px solid lightgray" }} align="center">
                      Cấu hình
                    </TableCell>
                    <TableCell style={{ border: "1px solid lightgray" }} align="center">
                      Giá trị cấu hình
                    </TableCell>
                    <TableCell style={{ border: "1px solid lightgray", width: 200 }} align="center">
                      {t("Trạng thái")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.length ? (
                    data.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell style={{ border: "1px solid lightgray" }} align="center">
                          {item.id}
                        </TableCell>
                        <TableCell style={{ border: "1px solid lightgray" }} align="left">
                          {item.label}
                        </TableCell>
                        <TableCell style={{ border: "1px solid lightgray" }} align="left">
                          {isEdit ? (
                            <CustomInput type={item.type} defaultValue={item.value} onChange={(value) => handleEdit(index, "value", value)} style={{ width: 262 }} />
                          ) : (
                            formatValue(item.value, item.type)
                          )}
                        </TableCell>
                        <TableCell style={{ border: "1px solid lightgray", width: 200 }} align="center">
                          <div className={`status ${item.status}`} style={{ color: statusColor[item.status] }}>
                            {t(item.status)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell style={{ border: "1px solid lightgray" }} align="center" colSpan={20}>
                        {t(isAllow ? "Không có dữ liệu" : "No permission")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[50, 100, 150]}
                      count={data.length || 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={setPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </div>
  );
}

const CustomInput = ({ type, ...props }) => {
  switch (type) {
    case "date":
      return <CustomDatePicker controlled={false} {...props} defaultValue={dayjs(props.defaultValue)} />;
    case "date-time":
      return <CustomDatePicker controlled={false} outputFormat="YYYY/MM/DD HH:mm" {...props} defaultValue={dayjs(props.defaultValue)} />;
    case "checkbox":
      return <Checkbox defaultChecked={props.defaultValue} onChange={(e, value) => props.onChange?.(value)} {...props} />;
    case "number":
      return <TextField type="number" {...props} onChange={(e) => props.onChange?.(+e.target.value)} />;
    default:
      return <TextField {...props} onChange={(e) => props.onChange?.(e.target.value)} />;
  }
};
const formatValue = (value, type) => {
  switch (type) {
    case "date":
      return value ? dayjs(value).format("DD/MM/YYYY") : "";
    case "date-time":
      return value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "";
    case "checkbox":
      return <Checkbox checked={value} />;
    default:
      return value;
  }
};
